/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import {
  Button,
  ConditionalWrapper,
  Grid,
  Icon,
  List,
  Modal,
  Spacer,
  useMediaQuery,
} from "@cd2/components/build/es6"
import { H3, H4, P, TextWrap } from "@cd2/text"
import { assert, IComponentCallback, titleize } from "@cd2/tools/build/es6"
import { format, formatDistanceToNowStrict, sub } from "date-fns"
import React, { useState } from "react"
import { Link } from "react-router-dom"
import { CommentsDisplay, CommentsForm } from "../../Components/CommentsForm"
import { brandColors, themeColors } from "../../Components/EOSThemeProvider"
import { useGlobalState } from "../../Components/GlobalStateProvider"
import { KeyValueList } from "../../Components/KeyValueList"
import { FloatingBox } from "../../Components/Misc/FloatingBox"
import { NotFoundPage } from "../../Components/NotFoundPage"
import { useNotification } from "../../Components/NotificationsBell"
import { PageLoadingScreen } from "../../Components/PageLoadingScreen"
import { PageWrapper } from "../../Components/PageWrapper"
import { IProductForSelect, ProductSelect } from "../../Components/ProductSelect"
import { QuickToolTip } from "../../Components/QuickTooltip"
import { UnexportedProductPromptWithButton } from "../../Components/SageApiExport/SageApiExportProductToSage"
import { TextWithNewLines } from "../../Components/TextWithNewLines"
import { Toolbar } from "../../Components/Toolbar"
import { icons } from "../../constants/icons"
import { pollingIntervals } from "../../constants/pollingIntervals"
import { useLockData } from "../../hooks/locks"
import { useAsyncCallback } from "../../hooks/useAsyncCallback"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useConfirmation } from "../../hooks/useConfirmation"
import { useAsyncInterval } from "../../hooks/useInterval"
import { useRedirectRoute } from "../../hooks/useRedirectRoute"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import { HoverCustomerLoader } from "../../hoverLoaders/HoverCustomerLoader"
import { HoverUserLoader } from "../../hoverLoaders/HoverUserLoader"
import { buildRoute, eRoutes, IERoute } from "../../routes"
import { calculateMarginFromPrice } from "../../stories/quoteOrders/quoteOrderUtils"
import { IRecord } from "../../types/schema"
import { getProductLocationPrioritized } from "../../utils/getProductLocations"
import { getProductSuggestedPrice } from "../../utils/getProductSuggestedPrice"
import { getTotals } from "../../utils/getQuoteOrderTotals"
import { useSettings } from "../../utils/useSettings"
import { formatPrice, generateTempId } from "../../utils/utils"
import { AlertBanners } from "../AlertBanners"
import { CustomerPriceLoader } from "../CustomerArea/components/CustomerPriceLoader"
import { ProductForm } from "../Stock/Products/ProductForm"
import { handleSkeemCreateProduct } from "../Stock/Products/ProductsNewPage"
import { ConfirmCancelDialogue } from "../../Components/ConfirmCancelDialogue"
import { CustomerSelectList2 } from "../../Components/SelectLists/CustomerSelectList2"
import { IJob, IJobShowPageProps } from "../JobsInternal/JobsShowPage"
import { useApi } from "../../hooks/useApi"

export function JobsShowPageComponent({ job, fetchJob, isLoading }: IJobShowPageProps) {
  const lockData = useLockData("jobs")
  const siteAlerts = useSiteAlerts()
  const isMobile = useMediaQuery({ maxWidth: 600 })
  const skeem = window.skeem
  const api = useApi()
  const { currency, currentUser } = useGlobalState()
  const {
    defaultStockLocation,
    warehouses,
    stockWarehouses,
    useExplicitAllocations,
    defaultIntercoWarehouse,
    deliveryOptions,
  } = useSettings()
  const [isDisplayingNotes, setIsDisplayingNotes] = useState(true)
  const redirect = useRedirectRoute()

  const [createProductNode, getCreateProduct] = useConfirmation<
    {
      product_master: IRecord<
        "product_masters",
        "name" | "stock_code" | "fortusLiveCode" | "hidden"
      >
    },
    boolean | IComponentCallback<typeof ProductForm, "onSubmit">
  >(({ onResolve, data }) => (
    <Modal
      render={() => (
        <ProductForm
          onSubmit={onResolve}
          data={data}
          onCancel={() => onResolve(false)}
          autoLoadProductMaster
          canEditProductMaster
          isNew
        />
      )}
      isOpen
    />
  ))

  const billingAddress = job?.customer?.addresses?.[0] ? job.customer.addresses[0] : undefined

  async function fetchProductPricing(
    product: IRecord<"products", "cost_price_pennies">,
    customerId: string,
  ) {
    const customerPrice = await getProductSuggestedPrice(
      skeem,
      { customer: { id: customerId } },
      product.id,
    )

    return {
      productId: product.id,
      price_pennies: customerPrice?.customerPrice ?? null,
      markUp: calculateMarginFromPrice(
        product.cost_price_pennies || undefined,
        customerPrice?.customerPrice ?? undefined,
      ),
    }
  }

  async function buildItems(
    items: IJob["items"],
    productData: {
      productId: string
      price_pennies: number | null
      markUp: number
    }[],
  ) {
    const productIds = items
      .filter(item => !!item.product)
      .map(v => v.product?.id)
      .filter(Boolean)
    // fresh: the allocations suggested below are decided from these figures
    const stockData = await api.productsStock(productIds as string[], {
      warehouseIds: stockWarehouses.map(v => v.id),
      fresh: true,
    })

    return items.map(item => {
      let allocation: number | undefined = undefined
      // if we have a product on this item && stock data for it
      if (item.product && useExplicitAllocations && item.product && stockData[item.product.id]) {
        if (!stockData[item.product.id].inStock) {
          allocation = 0
        } else if (stockData[item.product.id].inStock >= (item.quantity ?? 0)) {
          // If we have more stock than quantity requested, we can just assign the full quantity to allocate
          // then subtract the quantity we just allocated from in stock for future items
          allocation = item.quantity ?? 0
          stockData[item.product.id].inStock =
            stockData[item.product.id].inStock - (item.quantity ?? 0)
        } else if (stockData[item.product.id].inStock) {
          // If we have stock, but not the full quantity, assign the remaining stock
          // then set in stock to 0 as we just allocated it all
          allocation = stockData[item.product.id].inStock
          stockData[item.product.id].inStock = 0
        }
      }
      return {
        id: generateTempId(),
        stock_code: item.stock_code,
        quantity: item.quantity,
        price_pennies:
          productData.find(p => p.productId === item.product?.id)?.price_pennies ?? null,
        markUp: productData.find(p => p.productId === item.product?.id)?.markUp ?? null,
        cost_price_pennies: (item.cost_price_pennies || item.product?.cost_price_pennies) ?? 0,
        type: item.product ? 0 : 2,
        product: item.product,
        product_location: getProductLocationPrioritized(
          item.product?.product_locations ?? [],
          {
            defaultStockLocation: currentUser?.defaultWarehouse?.name ?? defaultStockLocation,
            branch: currentUser.branch,
            warehouses: warehouses,
            defaultIntercoWarehouse,
          },
          { isInterco: job?.customer?.internalCustomer },
        ),
        body: item.product?.line_item_description || item.product?.body || item.body,
        allocated_quantity: allocation,
      }
    })
  }

  async function generateJobData(job: IJob, redirectTo: IERoute) {
    let productData: {
      productId: string
      price_pennies: number | null
      markUp: number
    }[] = []

    if (isInternalJob) {
      // if made by a branch, use the price specified for the items
      productData = job.items
        .filter(x => x.product)
        .map(item => ({
          productId: item.product!.id,
          markUp: calculateMarginFromPrice(item.cost_price_pennies ?? 0, item.price_pennies ?? 0),
          price_pennies: item.price_pennies ?? null,
        }))
    } else {
      // if made by a customer ,load the up to date pricing
      productData = (
        await Promise.all(
          job.items.map(async item =>
            item.product ? await fetchProductPricing(item.product, job.customer?.id ?? "") : null,
          ),
        )
      ).filter(Boolean)
    }

    const items = await buildItems(job.items, productData.filter(Boolean))

    const deliveryAddress = {
      name: job.address_name,
      line_1: job.address_line_1,
      line_2: job.address_line_2,
      line_3: job.address_line_3,
      line_4: job.address_line_4,
      city: job.address_city,
      county: job.address_county,
      country: job.address_country,
      postcode: job.address_postcode,
      phone: job.address_phone,
      email: job.address_email,
    }
    const billingAddr = job?.customer?.addresses?.[0] ? job.customer.addresses[0] : undefined
    const billingAddress = billingAddr
      ? {
          name: billingAddr.name,
          line_1: billingAddr.line_1,
          line_2: billingAddr.line_2,
          line_3: billingAddr.line_3,
          line_4: billingAddr.line_4,
          city: billingAddr.city,
          county: billingAddr.county,
          country: billingAddr.country,
          postcode: billingAddr.postcode,
          phone: billingAddr.phone,
          email: billingAddr.email,
        }
      : undefined

    const data = {
      items: items,
      customer: job.customer,
      sub_customer: job.sub_customer,
      customer_reference: job.reference,
      contact: job.user?.contact,
      will_collect: job.shipTo === "collect",
      delivery_note_email: job.user?.email,
      delivery_method:
        job.shipTo === "collect"
          ? deliveryOptions.find(opt => opt.value.toLowerCase().includes("collect"))?.value
          : undefined,
      address: deliveryAddress,
      billing_address: billingAddress,
      job: {
        id: job.id,
      },
    }
    console.log("NEW JOB TO CREATE", data)
    redirect(redirectTo, {}, {}, { initialData: data })
  }

  const handleCreateQuoteOrder = useAsyncCallback(generateJobData)

  async function dismissPendingChanges() {
    if (recommendedProductSwaps.length > 0) {
      await skeem.mutate({
        jobs: {
          update: {
            id: job!.id,
            items: recommendedProductSwaps.map(v => ({
              update: { id: v.id, swapProduct: { set: null }, swapStatus: "" },
            })),
          },
        },
      })
    }
  }

  async function handleGenerateOrder() {
    if (!job) {
      return
    }
    await dismissPendingChanges()
    if (job.quote) {
      redirect("/quotes/:id/preview", { id: job.quote.id }, { qs: { genOrder: "1" } })
    } else {
      await handleCreateQuoteOrder(job, "/orders/new")
    }
  }

  async function handleGenerateQuote() {
    if (!job) {
      return
    }
    await dismissPendingChanges()
    await handleCreateQuoteOrder(job, "/quotes/new")
  }

  async function handleAssignProductToItem(
    product: IProductForSelect,
    item: IJob["items"][number],
  ) {
    await skeem.mutate({
      jobItems: {
        update: {
          id: item.id,
          product: { set: product.id },
          product_master: { set: product.product_master?.id ?? null },
          stock_code: product.stock_code,
          body: product.body,
          cost_price_pennies: product.cost_price_pennies,
        },
      },
    })
    fetchJob()
  }

  async function handleGenerateProduct(item: IJob["items"][number]) {
    const productMaster = item.product_master
    if (!productMaster) {
      siteAlerts.genericError("This product has no product master")
      return
    }
    const product = await getCreateProduct({ product_master: productMaster })
    if (product && typeof product === "object") {
      try {
        const record = await handleSkeemCreateProduct(skeem, product, currentUser)
        await skeem.mutate({
          jobItems: {
            update: {
              id: item.id,
              product: { set: record.id },
              product_master: { set: record.product_master?.id ?? null },
              stock_code: record.stock_code,
              body: record.body,
            },
          },
        })
        await fetchJob()
        siteAlerts.genericSuccess("Product created")
      } catch (e) {
        siteAlerts.genericError("Error creating product: " + e.message)
      }
      console.log("product", product)
    }
  }

  const handleAssignCustomer = useAsyncCallback(async () => {
    if (!job) {
      return
    }
    const id = await handleGetAssignCustomerId()
    if (!id) {
      return
    }
    await skeem.mutate({
      jobs: {
        update: {
          id: job.id,
          customer: { set: id },
        },
      },
    })
    await fetchJob()
    siteAlerts.genericSuccess("Customer assigned")
  })
  const [assignCustomerNode, handleGetAssignCustomerId] = useConfirmation<void, string | null>(
    ({ onResolve }) => {
      const [selectedCustomerId, setSelectedCustomerId] = useState<string>()
      return (
        <div
          css={css`
            .no-overflow {
              overflow: visible !important;
            }
          `}
        >
          <ConfirmCancelDialogue
            heading="Assign a customer"
            noOverflow={true}
            body={
              <React.Fragment>
                <CustomerSelectList2
                  label="Customer"
                  onChange={setSelectedCustomerId}
                  value={selectedCustomerId}
                  branchId={currentUser.branch?.id ?? ""}
                  disabled={!currentUser.branch?.id}
                />
              </React.Fragment>
            }
            onConfirm={() => onResolve(selectedCustomerId ?? null)}
            onCancel={() => onResolve(null)}
            isOpen
          />
        </div>
      )
    },
  )

  if (isLoading) {
    return <PageLoadingScreen />
  }

  if (!job) {
    return (
      <div>
        <NotFoundPage />
      </div>
    )
  }

  const recommendedProductSwaps = job.items.filter(
    item => !!item.swapProduct && item.swapStatus === "pending",
  )

  const isInternalJob = !!job.createdByBranch
  const title = isInternalJob
    ? `Quote Request ${job.identifier}`
    : `Customer Request ${job.identifier}`

  return (
    <React.Fragment>
      {assignCustomerNode}
      {!isInternalJob && (job.notes || job.comments.length > 0) && (
        <Modal
          render={() => (
            <Grid gap={8}>
              <H3>Customer left the following notes on this order</H3>
              <div
                css={css`
                  background: #f5f5f5;
                  padding: 16px;
                `}
              >
                <TextWithNewLines text={job?.notes} />
              </div>
              <Spacer />
              <H3>Comments</H3>
              <div
                css={css`
                  max-height: 400px;
                  overflow-y: scroll;
                `}
              >
                <CommentsDisplay comments={job.comments} />
              </div>
            </Grid>
          )}
          isOpen={isDisplayingNotes}
          onRequestClose={() => setIsDisplayingNotes(false)}
        />
      )}
      <Toolbar
        items={[
          !isInternalJob &&
            Toolbar.editLink({
              children: "Modify Products",
              to: buildRoute(
                job.createdByBranch
                  ? eRoutes["/quote-requests/:id/modify"]
                  : eRoutes["/jobs/:id/edit"],
                { id: job.id },
              ),
              disabled: !!job.isDraft || !job.customer,
            }),

          !job?.order &&
            !job?.quote && {
              icon: icons.new,
              children: "Create Quote",
              disabled:
                handleCreateQuoteOrder.running ||
                job.items.some(item => !item.product) ||
                !job.customer,
              tooltip: job.items.some(item => !item.product)
                ? "One or more items do not have a product"
                : undefined,
              confirmation:
                recommendedProductSwaps.length > 0
                  ? {
                      heading: "There are pending product suggestions",
                      body: (
                        <React.Fragment>
                          <P>
                            The customer has not responded to product change suggestions, are you
                            sure you want to proceed?
                          </P>
                          <Spacer />
                          <P weight={600}>Any outstanding changes will be dismissed.</P>
                        </React.Fragment>
                      ),
                    }
                  : undefined,
              onClick: handleGenerateQuote,
            },
          !job?.order &&
            !isInternalJob && {
              icon: icons.new,
              children: "Create Order",
              disabled:
                job.jobType === "quote" ||
                handleCreateQuoteOrder.running ||
                job.items.some(item => !item.product) ||
                !job.customer,
              tooltip: job.items.some(item => !item.product)
                ? "One or more items do not have a product"
                : job.jobType === "quote"
                ? "Cannot create an order for a quote request"
                : undefined,
              confirmation:
                recommendedProductSwaps.length > 0
                  ? {
                      heading: "There are pending product suggestions",
                      body: (
                        <React.Fragment>
                          <P>
                            The customer has not responded to product change suggestions, are you
                            sure you want to proceed?
                          </P>
                          <Spacer />
                          <P weight={600}>Any outstanding changes will be dismissed.</P>
                        </React.Fragment>
                      ),
                    }
                  : undefined,
              onClick: handleGenerateOrder,
            },
          job.quote && {
            to: buildRoute(eRoutes["/quotes/:id/preview"], { id: job?.quote.id }),
            children: "View quote",
            icon: icons.view,
            disabled: !job.customer,
          },
          job.order && {
            to: buildRoute(eRoutes["/orders/:id/preview"], { id: job?.order.id }),
            children: "View order",
            icon: icons.view,
            disabled: !job.customer,
          },
          job &&
            Toolbar.archive({
              currentUser,
              alerts: siteAlerts,
              id: job.id,
              isArchived: job.archived,
              lockData,
              model: "jobs",
              skeem,
              afterArchive: fetchJob,
            }),
        ].filter(Boolean)}
      />
      <PageWrapper
        breadcrumbPath={[isInternalJob ? "quoteRequests" : "jobs", { title }]}
        title={title}
        underTitleNode={
          <div>
            <AlertBanners
              banners={[
                !job.customer && {
                  message: (
                    <H4>Missing required customer. Please assign a customer to this job.</H4>
                  ),
                  actions: (
                    <Button
                      extendTheme={{ padding: { sides: 12, topAndBottom: 10 } }}
                      onClick={handleAssignCustomer}
                      disabled={handleAssignCustomer.running}
                    >
                      Assign customer
                    </Button>
                  ),
                  type: "error" as const,
                },
                job.isDraft && {
                  message: "This is a draft job and is for viewing only.",
                  type: "warning" as const,
                },
                job.jobType === "quote" && {
                  message: "A quote was requested for the following products ",
                  type: "info" as const,
                },
                job.refreshedFromQuote && {
                  message: (
                    <span>
                      This quote was refreshed from a previous expired quote{" "}
                      <a>{job.refreshedFromQuote.identifier}</a>
                    </span>
                  ),
                  to: buildRoute(eRoutes["/quotes/:id/preview"], { id: job.refreshedFromQuote.id }),
                  openToInNewTab: true,
                  type: "info" as const,
                },
                job.jobType === "order" && {
                  message: "An order was requested for the following products ",
                  type: "info" as const,
                },
              ].filter(Boolean)}
            />
          </div>
        }
      >
        {createProductNode}
        {(job.order || job.quote) && (
          <Grid columns={isMobile ? [{ fr: 1 }] : [{ fr: 1 }, { fr: 1 }]} gap={8}>
            {job.quote && (
              <FloatingBox
                header={
                  <Grid columns={["auto", "max-content"]}>
                    <H4>Quote</H4>
                  </Grid>
                }
              >
                <KeyValueList
                  labelWidth={120}
                  data={[
                    { label: "Identifier", value: job.quote.identifier },
                    {
                      label: "Quoted By",
                      value: job.quote.user && (
                        <HoverUserLoader
                          id={job.quote.user.id}
                          renderInlineLink="inline"
                          initialData={job.quote.user as any}
                        />
                      ),
                    },
                    {
                      label: "Carriage Price",
                      value: formatPrice(job.quote.carriage_pennies ?? 0, {
                        isPennies: true,
                        currency,
                      }),
                    },
                    {
                      label: "Total Price",
                      value: formatPrice(
                        getTotals(
                          job.quote.items,
                          job.quote.carriage_pennies,
                          job.customer?.tax_rate,
                        ).totalPricePennies,
                        { isPennies: true, currency },
                      ),
                    },
                    { label: "Created", value: { date: job.quote.created_at } },
                    {
                      label: "Last Edited At",
                      value:
                        job.quote.updated_at &&
                        (sub(new Date(), { days: 1 }) > new Date(job.quote.updated_at)
                          ? format(new Date(job.quote.updated_at), "dd/MM/yyyy")
                          : formatDistanceToNowStrict(new Date(job.quote.updated_at), {
                              addSuffix: true,
                            })),
                    },
                    {
                      label: "Customer notes",
                      value: job.notes ? <TextWithNewLines text={job.notes} /> : undefined,
                    },
                  ].filter(Boolean)}
                  inline
                />
              </FloatingBox>
            )}
            {job.order && (
              <FloatingBox header={<H4>Order</H4>}>
                <KeyValueList
                  labelWidth={120}
                  data={[
                    { label: "Identifier", value: job.order.identifier },
                    {
                      label: "Order By",
                      value: job.order.user && (
                        <HoverUserLoader
                          id={job.order.user.id}
                          renderInlineLink="inline"
                          initialData={job.order.user as any}
                        />
                      ),
                    },
                    {
                      label: "Carriage Price",
                      value: formatPrice(job.order.carriage_pennies ?? 0, {
                        isPennies: true,
                        currency,
                      }),
                    },
                    {
                      label: "Total Price",
                      value: formatPrice(
                        getTotals(
                          job.order.items,
                          job.order.carriage_pennies,
                          job.customer?.tax_rate,
                        ).totalPricePennies,
                        { isPennies: true, currency },
                      ),
                    },
                    {
                      label: "Status",
                      value: (
                        <List size={4} horizontal>
                          <QuickToolTip
                            content={job.order.pdf_generated ? "Printed" : "Not printed"}
                          >
                            <div
                              css={css`
                                display: inline-block;
                                border-radius: 2px;
                                color: white;
                                background-color: #ff9800;
                                opacity: ${job.order.pdf_generated ? 1 : 0.2};
                                padding: 5px;
                                width: 26px;
                                text-align: center;
                              `}
                            >
                              <Icon
                                icon={icons.print}
                                color={job.order.pdf_generated ? "black" : "white"}
                              />
                            </div>
                          </QuickToolTip>
                          <QuickToolTip
                            content={job.order?.delivery ? "Delivered" : "Not delivered"}
                          >
                            <div
                              css={css`
                                display: inline-block;
                                border-radius: 2px;
                                color: white;
                                background-color: #27ae60;
                                opacity: ${job.order?.delivery ? 1 : 0.2};
                                padding: 5px;
                                width: 26px;
                                text-align: center;
                              `}
                            >
                              <ConditionalWrapper
                                condition={!!job.order.delivery}
                                Wrapper={({ children }) => (
                                  <Link
                                    to={buildRoute(eRoutes["/deliveries/:id/preview"], {
                                      id: job.order?.delivery?.id,
                                    })}
                                  >
                                    {children}
                                  </Link>
                                )}
                              >
                                <Icon
                                  icon={icons.deliveries}
                                  color={job.order?.delivery ? "black" : "white"}
                                />
                              </ConditionalWrapper>
                            </div>
                          </QuickToolTip>
                        </List>
                      ),
                    },
                    { label: "Created", value: { date: job.order.created_at } },
                    {
                      label: "Last Edited At",
                      value:
                        job.order.updated_at &&
                        (sub(new Date(), { days: 1 }) > new Date(job.order.updated_at)
                          ? format(new Date(job.order.updated_at), "dd/MM/yyyy")
                          : formatDistanceToNowStrict(new Date(job.order.updated_at), {
                              addSuffix: true,
                            })),
                    },
                  ]}
                  inline
                />
              </FloatingBox>
            )}
          </Grid>
        )}
        {(job.order || job.quote) && <Spacer />}
        <Grid columns={[{ fr: 2 }, { fr: 1 }]} gap={8}>
          <KeyValueList
            labelWidth={120}
            data={[
              { label: "Created", value: { date: job.created_at } },
              { label: "Status", value: titleize(job.status) },
              { label: "Customer", value: <HoverCustomerLoader id={job.customer?.id} /> },
              job.sub_customer && {
                label: "Sub Customer",
                value: { hover: { model: "sub_customer", id: job.sub_customer.id } },
              },
              { label: "User", value: job.user?.name },
              isInternalJob && {
                label: "Quoting Branch",
                value: job.createdByBranch?.name,
              },
              { label: "Reference", value: job.reference },
              !isInternalJob && {
                label: "Requested Delivery Date",
                value: { date: job.requestedDeliveryDate, format: "dd/MM/yyyy" },
              },
              {
                label: "Fit hard drives to DVR/NVRs?",
                value: job.fit_hard_drives ? "Yes" : "No",
              },
              {
                label: "Delivery address",
                value:
                  job.shipTo === "collect" ? (
                    job.collectFromBranch ? (
                      `Collect from ${job.collectFromBranch.name}`
                    ) : (
                      "Customer will collect"
                    )
                  ) : (
                    <div>
                      {[
                        job.address_name,
                        job.address_line_1,
                        job.address_line_2,
                        job.address_line_3,
                        job.address_line_4,
                        job.address_city,
                        job.address_county,
                        job.address_country,
                        job.address_postcode,
                        job.address_phone,
                        job.address_email,
                      ]
                        .filter(Boolean)
                        .map((line, i) => (
                          <div key={i}>{line}</div>
                        ))}
                    </div>
                  ),
              },
              {
                label: "Billing address",
                value: (
                  <div>
                    {[
                      billingAddress?.name,
                      billingAddress?.line_1,
                      billingAddress?.line_2,
                      billingAddress?.line_3,
                      billingAddress?.line_4,
                      billingAddress?.city,
                      billingAddress?.county,
                      billingAddress?.country,
                      billingAddress?.postcode,
                      billingAddress?.phone,
                      billingAddress?.email,
                    ]
                      .filter(Boolean)
                      .map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                  </div>
                ),
              },
            ].filter(Boolean)}
            inline
          />
        </Grid>
        <Spacer size={32} />
        <TextWrap blockSpacing={0} weight={600}>
          <Grid
            columns={[250, { fr: 1 }, 100, 140].filter(Boolean)}
            css={css`
              padding: 0 16px;
              border: 1px solid transparent;
            `}
          >
            <P>Stock Item</P>
            <P>{null}</P>
            <P>Quantity</P>
            {!isInternalJob && job.customer?.canViewPricing && <P>Customer Pricing</P>}
            {isInternalJob && <P>Price</P>}
          </Grid>
        </TextWrap>
        <div>
          {job.items.map(item => (
            <div
              key={item.id}
              css={css`
                border: 1px solid ${brandColors.midGrey};
                padding: 16px;
              `}
            >
              <Grid columns={[250, { fr: 1 }, 100, 140].filter(Boolean)}>
                <div>
                  <P blockSpacing={0}>
                    <b>{item.product_master?.stock_code || item.stock_code}</b>
                  </P>
                  {item.stock_code &&
                    item.product_master &&
                    item.stock_code !== item.product_master.stock_code && (
                      <React.Fragment>
                        <Spacer size={4} />
                        <P blockSpacing={0}>(displayed as {item.stock_code})</P>
                      </React.Fragment>
                    )}
                </div>
                <P blockSpacing={0}>{item.product_master?.name || item.body}</P>
                <div>
                  <P blockSpacing={0}>{item.quantity}</P>
                </div>
                {isInternalJob ? (
                  <div>
                    {item.price_pennies
                      ? formatPrice(item.price_pennies, { isPennies: true, currency })
                      : "-"}
                  </div>
                ) : (
                  <React.Fragment>
                    {item.product ? (
                      <CustomerPriceLoader
                        productId={item.product.id}
                        customerId={job.customer?.id}
                      />
                    ) : (
                      <div>N/A</div>
                    )}
                  </React.Fragment>
                )}
              </Grid>
              {!item.product && (
                <div
                  css={css`
                    margin-top: 8px;
                    padding: 8px;
                    border: 1px solid ${themeColors.lightOrange};
                    border-radius: 4px;
                  `}
                >
                  <P weight={600} size="small" blockSpacing={1}>
                    This product is not present in your Sage company. Please either select a product
                    to assign to it, or Add a new product to your Sage company.
                  </P>
                  <Grid
                    columns={{
                      repeat: { times: "auto-fill", size: { min: 300, max: { fr: 1 } } },
                    }}
                    gap={16}
                  >
                    <ProductSelect onChange={product => handleAssignProductToItem(product, item)} />
                    {currentUser.canCreateSageProducts && (
                      <Button onClick={() => handleGenerateProduct(item)}>
                        Add product to Sage
                      </Button>
                    )}
                  </Grid>
                </div>
              )}
              {!item.product &&
                item.product_master &&
                item.product_master?.available_at_branches.length > 0 && (
                  <div>
                    <Spacer size={8} />
                    <H4>{item.product_master.stock_code} is sold at these branches...</H4>
                    {item.product_master.available_at_branches.map(branch => (
                      <P key={branch.id}>{branch.name}</P>
                    ))}
                  </div>
                )}
              <UnexportedProductPromptWithButton product={item.product} afterExport={fetchJob} />
            </div>
          ))}
        </div>

        {!isInternalJob && !job.isDraft && job.customer && (
          <div>
            <JobCommentsBox jobId={job.id} sendingTo="customer" />
          </div>
        )}
      </PageWrapper>
    </React.Fragment>
  )
}

type IJobWithComments = IRecord<"jobs", "customer" | "identifier" | "reference"> & {
  comments: IRecord<"comments", "body" | "user" | "created_at" | "alias">[]
}

interface IJobCommentsBoxProps {
  jobId: string
  sendingTo: "branch" | "customer"
  maxMessageContainerHeight?: number
  readOnly?: boolean
}

export function JobCommentsBox({
  jobId,
  sendingTo,
  maxMessageContainerHeight,
  readOnly,
}: IJobCommentsBoxProps) {
  const jobComments = useJobComments(jobId, { sendingTo })
  const { currentUser } = useGlobalState()

  if (!jobComments.isLoaded) {
    return <div />
  }

  return (
    <CommentsForm
      comments={jobComments.comments}
      onCommentsSubmit={jobComments.handleCommentSubmit}
      title={{
        body: `Leave a comment, this is viewable by the ${
          currentUser.customer ? "branch" : "customer"
        }`,
        heading: `Message with the ${currentUser.customer ? "branch" : "customer"}`,
      }}
      readOnly={readOnly}
      maxMessageContainerHeight={maxMessageContainerHeight}
    />
  )
}

export function useJobComments(
  jobId?: string,
  opts?: { sendingTo: "branch" | "customer"; sendCommentWithoutNotification?: boolean },
) {
  const { currentUser } = useGlobalState()
  const skeem = window.skeem
  const { sendCommentOnJobNotification } = useNotification()
  const [job, setJob] = useState<IJobWithComments | null>(null)
  const [isLoaded, setIsLoaded] = useState<boolean>(false)

  async function fetchJob(jobId: string) {
    if (!jobId) {
      return
    }
    const job = await skeem.fetch2<IJobWithComments>({
      first: {
        name: "jobs",
        attributes: [
          "customer",
          "identifier",
          "reference",
          {
            name: "comments",
            attributes: ["body", "created_at", "user", "alias"],
            sort: { by: "created_at", direction: "asc" },
          },
        ],
        filter: { matches: { id: jobId } },
      },
    })
    setJob(job)
    setIsLoaded(true)
    return job
  }

  async function handleCommentSubmit(jobId: string, body: string) {
    let jobRecord = job || undefined
    if (!job) {
      jobRecord = await fetchJob(jobId)
    }
    if (!jobRecord) {
      return
    }
    assert(jobRecord.customer)
    await skeem.mutate({
      comments: {
        create: {
          body: body,
          alias: `${currentUser.name} from ${
            currentUser.customer ? currentUser.customer.name : currentUser.branch.name
          }`,
          user: { set: currentUser.id },
          job: { set: jobId },
        },
      },
    })

    if (opts?.sendingTo) {
      if (opts.sendingTo === "branch") {
        await skeem.mutate({ jobs: { update: { id: jobRecord.id, hasUnreadComments: true } } })
      }
      if (!opts?.sendCommentWithoutNotification) {
        sendCommentOnJobNotification(jobRecord.id, opts.sendingTo)
      }
    }

    await fetchJob(jobId)
  }

  useAsyncEffect(async () => {
    await fetchJob(jobId ?? "")
  }, [])

  useAsyncInterval(async () => {
    await fetchJob(jobId ?? "")
  }, pollingIntervals.jobsShowRefetch)

  return {
    handleCommentSubmit: (body: string) => handleCommentSubmit(jobId ?? "", body),
    handleCommentSubmitForJob: handleCommentSubmit,
    isLoaded,
    comments: job?.comments ?? [],
  }
}
